import * as React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { tw } from 'utils/classnames'

import { ClientOnly } from '../client-only'

interface ShowMoreTextProps {
  children: string
  maxLines?: number
  expandButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  showMore?: React.ReactNode
  showLess?: React.ReactNode
}

export function ShowMoreText(props: ShowMoreTextProps) {
  const {
    children,
    maxLines = 2,
    expandButtonProps = {},
    showMore = 'Read more',
    showLess = 'Read less',
  } = props
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [maxHeight, setMaxHeight] = React.useState<number>(0)
  const [collapsedHeight, setCollapsedHeight] = React.useState<number>(0)
  const [style, setStyle] = React.useState<React.CSSProperties>({
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
  })

  React.useEffect(() => {
    if (contentRef.current) {
      const lineHeight = parseFloat(getComputedStyle(contentRef.current).lineHeight)
      setCollapsedHeight(lineHeight * maxLines)
      const initialHeight = isExpanded ? contentRef.current.scrollHeight : lineHeight * maxLines
      setMaxHeight(initialHeight)
    }
  }, [children, maxLines, isExpanded])

  const toggleExpanded = () => {
    if (contentRef.current) {
      const targetHeight = isExpanded ? collapsedHeight : contentRef.current.scrollHeight
      setMaxHeight(targetHeight)
      setIsExpanded(!isExpanded)
    }
  }

  const { className: btnClassName = '', ...restButtonProps } = expandButtonProps

  return (
    <ClientOnly
      fallback={
        <>
          <div className="max-h-[48px] overflow-hidden" ref={contentRef}>
            {children}
          </div>
          <p
            className={tw('mt-2', btnClassName)}
            {...(restButtonProps as React.HTMLAttributes<HTMLParagraphElement>)}
          >
            {showMore}
          </p>
        </>
      }
    >
      <AnimatePresence initial={false}>
        <motion.div
          initial={false}
          animate={{ maxHeight }}
          exit={{ maxHeight }}
          transition={{ duration: 0.3 }}
          style={style}
          onAnimationStart={() => {
            if (isExpanded) {
              setStyle(prevStyle => ({
                ...prevStyle,
                WebkitLineClamp: 'initial',
              }))
            }
          }}
          onAnimationComplete={() => {
            if (!isExpanded) {
              setStyle(prevStyle => ({
                ...prevStyle,
                WebkitLineClamp: maxLines,
              }))
            }
          }}
        >
          <div ref={contentRef}>{children}</div>
        </motion.div>
      </AnimatePresence>
      <button
        onClick={toggleExpanded}
        className={tw('mt-2 focus:outline-none focus-visible:outline', btnClassName)}
        {...restButtonProps}
      >
        {isExpanded ? showLess : showMore}
      </button>
    </ClientOnly>
  )
}
